import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import config from "../../../lib/config";
import { siteApi } from "../../../lib/api";
import { StyledRandomSlotPicker, StyledRandomSlotPickerOverlay } from "./StyledRandomSlotPicker";

const RANDOM_SLOT_LIMIT = 30;
const RANDOM_ROLL_INTERVAL_MS = 86;
const RANDOM_ROLL_DURATION_MS = 2200;

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const getTotalCount = (response) => {
  const payload = response?.data;
  const count = Number(
    payload?.count ??
      payload?.total ??
      payload?.total_count ??
      payload?.totalCount ??
      payload?.pagination?.total ??
      0
  );

  return Number.isFinite(count) && count > 0 ? count : getRows(response).length;
};

const normalizeTitle = (value = "") =>
  value
    .replace(/\bBTI\b/gi, "BTI")
    .replace(/\bSABA\b/gi, "SABA")
    .replace(/\s+/g, " ")
    .trim();

const getGameTitle = (game) =>
  normalizeTitle(game?.game_name_ko || game?.game_name_kr || game?.name_ko || game?.game_name || "슬롯게임");

const getGameVendorTitle = (game, fallback = "") =>
  normalizeTitle(
    game?.vendor_name_kr ||
      game?.vendor_name_ko ||
      game?.provider_name_kr ||
      game?.provider_name_ko ||
      game?.display_vendor_name ||
      game?.vendor_display_name ||
      game?.vendor_name ||
      game?.provider_name ||
      fallback ||
      game?.vendor ||
      "슬롯 게임사"
  );

const getGameImage = (game) =>
  resolveAssetUrl(game?.game_image || game?.image || game?.thumbnail || game?.thumbnail_url);

const getGameId = (game) => game?.game_id || "";

const getGameVendorName = (game) => game?.vendor || game?.vendor_name || "";

const getGameKey = (game) => `${getGameId(game)}:${getGameVendorName(game)}`;

const getActiveVendorNames = (vendors) => {
  const seen = new Set();

  return vendors.reduce((names, vendor) => {
    const vendorName =
      typeof vendor?.vendor_name === "string" ? vendor.vendor_name.trim() : "";
    if (!vendorName || vendor?.vendor_list == null || seen.has(vendorName)) return names;
    seen.add(vendorName);
    names.push(vendorName);
    return names;
  }, []);
};

const dedupeEligibleRandomGames = (games, eligibleVendorNames) => {
  const seen = new Set();

  return games.filter((game) => {
    const gameId = getGameId(game);
    const vendorName = getGameVendorName(game);
    if (
      !gameId ||
      !vendorName ||
      game?.is_active !== true ||
      game?.game_enabled !== true ||
      Boolean(game?.deleted_at) ||
      !eligibleVendorNames.has(vendorName)
    ) {
      return false;
    }

    const key = getGameKey(game);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const RandomSlotPicker = ({
  selectedVendor,
  selectedVendorTitle,
  activeVendors = [],
  warmupCandidates = [],
  launchingGameId,
  onLaunchGame,
}) => {
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const requestRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [previewGame, setPreviewGame] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [error, setError] = useState("");
  const activeVendorNames = useMemo(
    () => getActiveVendorNames(activeVendors),
    [activeVendors]
  );
  const activeVendorNameSet = useMemo(
    () => new Set(activeVendorNames),
    [activeVendorNames]
  );
  const selectedVendorIsActive =
    !selectedVendor || activeVendorNameSet.has(selectedVendor);
  const eligibleVendorNameSet = useMemo(
    () =>
      selectedVendor && activeVendorNameSet.has(selectedVendor)
        ? new Set([selectedVendor])
        : selectedVendor
          ? new Set()
          : activeVendorNameSet,
    [activeVendorNameSet, selectedVendor]
  );

  const clearRollTimers = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearRollTimers(), [clearRollTimers]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const closePicker = useCallback(() => {
    requestRef.current += 1;
    clearRollTimers();
    setIsOpen(false);
    setIsLoading(false);
    setIsRolling(false);
    setError("");
  }, [clearRollTimers]);

  const startRoll = useCallback(
    (nextCandidates, preferredFinalIndex) => {
      clearRollTimers();
      if (!nextCandidates.length) return;

      const finalIndex =
        Number.isInteger(preferredFinalIndex) && preferredFinalIndex >= 0
          ? Math.min(preferredFinalIndex, nextCandidates.length - 1)
          : Math.floor(Math.random() * nextCandidates.length);
      let cursor = finalIndex + 1;

      setSelectedGame(null);
      setPreviewGame(nextCandidates[cursor % nextCandidates.length]);
      setIsRolling(true);
      setError("");

      intervalRef.current = window.setInterval(() => {
        setPreviewGame(nextCandidates[cursor % nextCandidates.length]);
        cursor += 1;
      }, RANDOM_ROLL_INTERVAL_MS);

      timeoutRef.current = window.setTimeout(() => {
        clearRollTimers();
        const finalGame = nextCandidates[finalIndex];
        setPreviewGame(finalGame);
        setSelectedGame(finalGame);
        setIsRolling(false);
      }, RANDOM_ROLL_DURATION_MS);
    },
    [clearRollTimers]
  );

  const startWarmupRoll = useCallback(
    (nextCandidates) => {
      clearRollTimers();
      if (!nextCandidates.length) {
        setPreviewGame(null);
        setIsRolling(false);
        return;
      }

      let cursor = Math.floor(Math.random() * nextCandidates.length);
      setSelectedGame(null);
      setPreviewGame(nextCandidates[cursor % nextCandidates.length]);
      setIsRolling(true);
      setError("");

      intervalRef.current = window.setInterval(() => {
        cursor += 1;
        setPreviewGame(nextCandidates[cursor % nextCandidates.length]);
      }, RANDOM_ROLL_INTERVAL_MS);
    },
    [clearRollTimers]
  );

  const loadCandidatesAndRoll = useCallback(async () => {
    const requestId = ++requestRef.current;
    const warmupRows = dedupeEligibleRandomGames(
      warmupCandidates,
      eligibleVendorNameSet
    ).slice(0, RANDOM_SLOT_LIMIT);
    setIsLoading(true);
    setSelectedGame(null);
    setError("");
    clearRollTimers();
    startWarmupRoll(warmupRows);

    const stopPendingRoll = () => {
      clearRollTimers();
      setIsRolling(false);
    };

    try {
      if (!selectedVendorIsActive) {
        setError("현재 활성화된 슬롯 게임사가 아닙니다.");
        stopPendingRoll();
        return;
      }

      if (!activeVendorNames.length) {
        setError("선택할 수 있는 활성 슬롯 게임사가 없습니다.");
        stopPendingRoll();
        return;
      }

      const baseParams = {
        page: 1,
        limit: RANDOM_SLOT_LIMIT,
        game_type: "slots",
        order_by_name: "sequence",
        order_by_type: "ASC",
      };

      const firstVendorIndex = selectedVendor
        ? activeVendorNames.indexOf(selectedVendor)
        : Math.floor(Math.random() * activeVendorNames.length);
      const candidateVendors = selectedVendor
        ? [selectedVendor]
        : [
            ...activeVendorNames.slice(firstVendorIndex),
            ...activeVendorNames.slice(0, firstVendorIndex),
          ];

      for (const vendorName of candidateVendors) {
        const countResponse = await siteApi.getGames({
          ...baseParams,
          vendor: vendorName,
          page: 1,
          limit: 1,
        });
        if (requestId !== requestRef.current) return;

        if (!countResponse.success) {
          continue;
        }

        const totalCount = getTotalCount(countResponse);
        if (!totalCount) continue;

        const randomIndex = Math.floor(Math.random() * totalCount);
        const randomPage = Math.floor(randomIndex / RANDOM_SLOT_LIMIT) + 1;
        const response = await siteApi.getGames({
          ...baseParams,
          vendor: vendorName,
          page: randomPage,
          limit: RANDOM_SLOT_LIMIT,
        });
        if (requestId !== requestRef.current) return;
        if (!response.success) continue;

        const rows = dedupeEligibleRandomGames(
          getRows(response),
          new Set([vendorName])
        ).slice(0, RANDOM_SLOT_LIMIT);
        if (!rows.length) continue;

        startRoll(rows, randomIndex % RANDOM_SLOT_LIMIT);
        return;
      }

      setError("선택할 슬롯 게임이 없습니다.");
      stopPendingRoll();
    } catch {
      if (requestId === requestRef.current) {
        setError("랜덤 슬롯 후보를 불러오지 못했습니다.");
        stopPendingRoll();
      }
    } finally {
      if (requestId === requestRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    activeVendorNames,
    clearRollTimers,
    eligibleVendorNameSet,
    selectedVendor,
    selectedVendorIsActive,
    startRoll,
    startWarmupRoll,
    warmupCandidates,
  ]);

  const openPicker = useCallback(() => {
    setIsOpen(true);
    loadCandidatesAndRoll();
  }, [loadCandidatesAndRoll]);

  const reroll = useCallback(() => {
    if (isLoading || isRolling) return;
    loadCandidatesAndRoll();
  }, [isLoading, isRolling, loadCandidatesAndRoll]);

  const launchSelectedGame = useCallback(async () => {
    if (!selectedGame || isRolling || isLoading) return;
    if (!dedupeEligibleRandomGames([selectedGame], eligibleVendorNameSet).length) {
      setSelectedGame(null);
      setPreviewGame(null);
      setError("현재 실행할 수 없는 슬롯 게임입니다.");
      return;
    }

    const result = await onLaunchGame(
      selectedGame,
      getGameTitle(selectedGame),
      getGameVendorTitle(selectedGame, selectedVendorTitle || "슬롯게임")
    );
    if (result?.success) closePicker();
  }, [
    closePicker,
    eligibleVendorNameSet,
    isLoading,
    isRolling,
    onLaunchGame,
    selectedGame,
    selectedVendorTitle,
  ]);

  const activeGame = previewGame || selectedGame;
  const activeTitle = activeGame ? getGameTitle(activeGame) : "랜덤 슬롯";
  const activeVendorTitle = activeGame ? getGameVendorTitle(activeGame, selectedVendorTitle) : "";
  const activeImage = activeGame ? getGameImage(activeGame) : "";
  const isLaunching = selectedGame && launchingGameId === getGameKey(selectedGame);

  return (
    <>
      <StyledRandomSlotPicker>
        <button type="button" className="random-slot-trigger" onClick={openPicker}>
          <span className="random-slot-trigger-icon" aria-hidden="true" />
          랜덤 슬롯 플레이
        </button>
      </StyledRandomSlotPicker>

      {isOpen
        ? createPortal(
            <StyledRandomSlotPickerOverlay
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closePicker();
              }}
            >
              <section className="random-slot-modal" role="dialog" aria-modal="true" aria-label="랜덤 슬롯 플레이">
                <header className="random-slot-header">
                  <div>
                    <span>RANDOM SLOT</span>
                    <h2>랜덤 슬롯 플레이</h2>
                    <p>
                      {selectedVendor
                        ? `${selectedVendorTitle || "선택 게임사"} 활성 슬롯 기준`
                        : "전체 활성 슬롯 기준"}
                    </p>
                  </div>
                  <button type="button" className="random-slot-close" aria-label="닫기" onClick={closePicker}>
                    ×
                  </button>
                </header>

                <div className="random-slot-stage">
                  <figure className={`random-slot-card${isRolling ? " is-rolling" : ""}`}>
                    {activeImage ? (
                      <img src={activeImage} alt={activeTitle} />
                    ) : (
                      <span className="random-slot-empty">{isLoading ? "불러오는 중" : "랜덤 슬롯"}</span>
                    )}
                    <figcaption>
                      <span>{isRolling ? "선택 중" : selectedGame ? "선택 완료" : "READY"}</span>
                      <strong>{activeTitle}</strong>
                      {selectedGame && activeVendorTitle ? <small>{activeVendorTitle}</small> : null}
                    </figcaption>
                  </figure>
                </div>

                {error ? <div className="random-slot-error">{error}</div> : null}

                <div className="random-slot-actions">
                  <button
                    type="button"
                    className="random-slot-secondary"
                    onClick={reroll}
                    disabled={isLoading || isRolling}
                  >
                    다시 돌리기
                  </button>
                  <button
                    type="button"
                    className="random-slot-primary"
                    onClick={launchSelectedGame}
                    disabled={!selectedGame || isLoading || isRolling || isLaunching}
                  >
                    {isLaunching ? "실행 중" : "실행하기"}
                  </button>
                </div>
              </section>
            </StyledRandomSlotPickerOverlay>,
            document.body
          )
        : null}
    </>
  );
};

export default RandomSlotPicker;
